'use client';

import { useRealtimeRun } from '@trigger.dev/react-hooks';
import { useEffect, useRef, useState } from 'react';
import type { AIAnalysisResult, CustomAnalysisResult } from '@timeblock/tasks';
import { captureException } from '@/lib/posthog-tracking';

type AnalysisResult = AIAnalysisResult | CustomAnalysisResult;

interface PersistedAssessmentResponse {
  analysisResult?: AnalysisResult;
}

const FALLBACK_POLL_INTERVAL_MS = 2_000;

function assessmentResultPath(runId: string): string {
  const configuredBasePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
  const basePath = configuredBasePath === '/' ? '' : configuredBasePath.replace(/\/$/, '');
  return `${basePath}/api/assessment/${encodeURIComponent(runId)}`;
}

export function useAssessmentAnalysis(
  runId: string | undefined,
  publicAccessToken: string | undefined,
  assessmentTypeHint?: string
) {
  const { run, error } = useRealtimeRun(runId, {
    enabled: !!runId && !!publicAccessToken,
    accessToken: publicAccessToken, // Use accessToken parameter as per docs
  });
  const [persistedAnalysisResult, setPersistedAnalysisResult] = useState<AnalysisResult>();
  const [fallbackError, setFallbackError] = useState<Error>();
  const reportedRealtimeErrorRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!error) return;

    const errorName = error instanceof Error ? error.name : 'UnknownError';
    const errorMessage = error instanceof Error ? error.message : String(error);
    const dedupeKey = `${errorName}:${errorMessage}`;

    if (reportedRealtimeErrorRef.current === dedupeKey) return;
    reportedRealtimeErrorRef.current = dedupeKey;

    captureException(error, {
      source: 'third_party_integration',
      severity: 'medium',
      assessmentType: assessmentTypeHint,
      additionalData: {
        integration: 'trigger_realtime',
        operation: 'subscribe_to_assessment_run',
        realtime_error_name: errorName,
        run_id_present: Boolean(runId),
        access_token_present: Boolean(publicAccessToken),
        persisted_result_fallback_enabled: true,
      },
    });
  }, [error, assessmentTypeHint, runId, publicAccessToken]);

  useEffect(() => {
    setPersistedAnalysisResult(undefined);
    setFallbackError(undefined);

    if (!runId || !publicAccessToken) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const pollPersistedResult = async () => {
      try {
        const response = await fetch(assessmentResultPath(runId), {
          cache: 'no-store',
        });

        if (response.ok) {
          const data = (await response.json()) as PersistedAssessmentResponse;
          if (data.analysisResult) {
            if (!cancelled) setPersistedAnalysisResult(data.analysisResult);
            return;
          }
        } else if (response.status !== 404) {
          throw new Error(`Assessment result lookup failed (${response.status})`);
        }
      } catch (pollError) {
        if (!cancelled) {
          setFallbackError(
            pollError instanceof Error ? pollError : new Error('Assessment result lookup failed')
          );
        }
      }

      if (!cancelled) {
        timeoutId = setTimeout(pollPersistedResult, FALLBACK_POLL_INTERVAL_MS);
      }
    };

    void pollPersistedResult();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [runId, publicAccessToken]);
  
  // Debug logging
  if (run) {
    console.log('📊 Run data:', {
      id: run.id,
      status: run.status,
      metadata: run.metadata,
      output: run.output,
    });
  }
  
  // Get analysis result and response type from metadata
  let analysisResult = run?.metadata?.analysisResult as AnalysisResult | undefined;
  const responseType = run?.metadata?.responseType as string | undefined;
  const assessmentType = run?.metadata?.assessmentType as string | undefined;
  
  // If not in metadata, check run output (backwards compatibility)
  if (!analysisResult && run?.output) {
    analysisResult = run.output as AnalysisResult;
  }

  analysisResult ??= persistedAnalysisResult;
  
  // For multi-day email sequences, the parent task stays in EXECUTING status
  // We should consider the analysis complete once we have the result in metadata
  const hasAnalysisResult = !!analysisResult;
  const isComplete = hasAnalysisResult || run?.status === 'COMPLETED';
  const isAnalyzing = run?.status === 'EXECUTING' && !hasAnalysisResult;
  
  return {
    run,
    isAnalyzing,
    isComplete,
    analysisResult,
    responseType,
    assessmentType,
    error: error ?? fallbackError,
  };
}
